import { gql } from '@apollo/client';
import { INSTANCE_FIELDS_FRAGMENT } from './fragments';

/**
 * Create new instance
 */
export const CREATE_INSTANCE = gql`
  ${INSTANCE_FIELDS_FRAGMENT}
  mutation CreateInstance($tenantId: String!, $input: CreateInstanceInput!) {
    createInstance(tenantId: $tenantId, input: $input) {
      ...InstanceFields
    }
  }
`;

/**
 * Start instance execution
 */
export const START_INSTANCE = gql`
  ${INSTANCE_FIELDS_FRAGMENT}
  mutation StartInstance($tenantId: String!, $instanceId: String!) {
    startInstance(tenantId: $tenantId, instanceId: $instanceId) {
      ...InstanceFields
    }
  }
`;

/**
 * Pause running instance
 */
export const PAUSE_INSTANCE = gql`
  ${INSTANCE_FIELDS_FRAGMENT}
  mutation PauseInstance($tenantId: String!, $instanceId: String!) {
    pauseInstance(tenantId: $tenantId, instanceId: $instanceId) {
      ...InstanceFields
    }
  }
`;

/**
 * Resume paused instance
 */
export const RESUME_INSTANCE = gql`
  ${INSTANCE_FIELDS_FRAGMENT}
  mutation ResumeInstance($tenantId: String!, $instanceId: String!) {
    resumeInstance(tenantId: $tenantId, instanceId: $instanceId) {
      ...InstanceFields
    }
  }
`;

/**
 * Complete instance with output data
 */
export const COMPLETE_INSTANCE = gql`
  ${INSTANCE_FIELDS_FRAGMENT}
  mutation CompleteInstance($tenantId: String!, $instanceId: String!, $outputData: JSON!) {
    completeInstance(tenantId: $tenantId, instanceId: $instanceId, outputData: $outputData) {
      ...InstanceFields
    }
  }
`;

/**
 * Fail instance with error information
 */
export const FAIL_INSTANCE = gql`
  ${INSTANCE_FIELDS_FRAGMENT}
  mutation FailInstance($tenantId: String!, $instanceId: String!, $input: RecordErrorInput!) {
    failInstance(tenantId: $tenantId, instanceId: $instanceId, input: $input) {
      ...InstanceFields
    }
  }
`;

/**
 * Cancel instance execution
 */
export const CANCEL_INSTANCE = gql`
  ${INSTANCE_FIELDS_FRAGMENT}
  mutation CancelInstance($tenantId: String!, $instanceId: String!) {
    cancelInstance(tenantId: $tenantId, instanceId: $instanceId) {
      ...InstanceFields
    }
  }
`;

/**
 * Update instance progress
 */
export const UPDATE_INSTANCE_PROGRESS = gql`
  ${INSTANCE_FIELDS_FRAGMENT}
  mutation UpdateInstanceProgress(
    $tenantId: String!
    $instanceId: String!
    $input: UpdateProgressInput!
  ) {
    updateInstanceProgress(tenantId: $tenantId, instanceId: $instanceId, input: $input) {
      ...InstanceFields
    }
  }
`;

/**
 * Update instance metadata
 */
export const UPDATE_INSTANCE = gql`
  ${INSTANCE_FIELDS_FRAGMENT}
  mutation UpdateInstance($tenantId: String!, $instanceId: String!, $input: UpdateInstanceInput!) {
    updateInstance(tenantId: $tenantId, instanceId: $instanceId, input: $input) {
      ...InstanceFields
    }
  }
`;

/**
 * Record event for instance
 */
export const RECORD_INSTANCE_EVENT = gql`
  ${INSTANCE_FIELDS_FRAGMENT}
  mutation RecordInstanceEvent(
    $tenantId: String!
    $instanceId: String!
    $eventId: String!
    $eventType: String!
  ) {
    recordInstanceEvent(
      tenantId: $tenantId
      instanceId: $instanceId
      eventId: $eventId
      eventType: $eventType
    ) {
      ...InstanceFields
    }
  }
`;

/**
 * Transition instance to new state (Pure Append-Only Architecture)
 *
 * Creates NEW instance version with:
 * - Incremented version number
 * - parent_id linking to current instance
 * - root_instance_id preserved from V1
 * - Updated status and current_state
 *
 * @example
 * ```typescript
 * const newInstance = await client.mutate<Record<string, any>>({
 *   mutation: TRANSITION_INSTANCE,
 *   variables: {
 *     tenantId: "healthcare-tenant",
 *     workflowDefinitionId: "wf_def_123",
 *     instanceId: "inst_abc",
 *     input: {
 *       newStatus: "RUNNING",
 *       newState: "processing_claim",
 *       trigger: "user_started_workflow"
 *     }
 *   }
 * });
 * // newInstance.version = currentInstance.version + 1
 * // newInstance.parentId = currentInstance.id
 * // newInstance.rootInstanceId = currentInstance.rootInstanceId
 * ```
 */
export const TRANSITION_INSTANCE = gql`
  ${INSTANCE_FIELDS_FRAGMENT}
  mutation TransitionInstance(
    $tenantId: String!
    $workflowDefinitionId: String!
    $instanceId: String!
    $input: TransitionInstanceInput!
  ) {
    transitionInstance(
      tenantId: $tenantId
      workflowDefinitionId: $workflowDefinitionId
      instanceId: $instanceId
      input: $input
    ) {
      ...InstanceFields
    }
  }
`;
